import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Plus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/apiClient";
import { T } from "../../lib/testIds";

const STAGE_LABELS = {
  new_lead: "New lead", contacted: "Contacted", quoted: "Quoted",
  scheduled: "Scheduled", job_complete: "Job complete", repeat_recurring: "Repeat/Recurring",
};

export default function Contacts() {
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [newContact, setNewContact] = useState({ first_name: "", last_name: "", email: "", phone: "", address: "", zip: "" });
  const [saving, setSaving] = useState(false);

  const load = () => { api.get("/contacts", { params: search ? { search } : {} }).then(r => setRows(r.data)); };
  useEffect(() => { load(); }, [search]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/contacts", newContact);
      toast.success("Contact added");
      setAddOpen(false); setNewContact({ first_name: "", last_name: "", email: "", phone: "", address: "", zip: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-[Outfit] font-extrabold text-3xl text-[color:var(--owc-navy)]">Contacts</h1>
          <p className="text-slate-600 mt-1">Every lead, customer, and past client in one place.</p>
        </div>
        <button data-testid={T.contacts.addBtn} onClick={() => setAddOpen(true)} className="owc-btn-accent text-sm inline-flex items-center gap-2"><Plus className="h-4 w-4" /> Add contact</button>
      </div>

      <div className="owc-card p-4">
        <div className="flex items-center gap-2 border-b border-slate-100 pb-3 mb-3">
          <Search className="h-4 w-4 text-slate-400" />
          <input data-testid={T.contacts.search} placeholder="Search by name, email, phone…" className="owc-input py-2" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-bold uppercase text-slate-500">
                <th className="py-3">Name</th><th>Contact</th><th>Address</th><th>Stage</th><th>LTV</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(c => (
                <tr key={c.id} data-testid={T.contacts.row(c.id)} className="hover:bg-slate-50">
                  <td className="py-3 pr-4">
                    <Link to={`/admin/contacts/${c.id}`} className="font-semibold text-[color:var(--owc-navy)] hover:text-[color:var(--owc-sky)]">{c.name}</Link>
                    <div className="text-xs text-slate-500">{(c.tags || []).join(", ") || "—"}</div>
                  </td>
                  <td className="py-3 pr-4">
                    <div>{c.email || "—"}</div>
                    <div className="text-xs text-slate-500">{c.phone || ""}</div>
                  </td>
                  <td className="py-3 pr-4">{c.address ? `${c.address}, ${c.city || ""} ${c.zip || ""}` : "—"}</td>
                  <td className="py-3 pr-4"><span className="owc-badge-blue">{STAGE_LABELS[c.stage] || c.stage}</span></td>
                  <td className="py-3 pr-4 font-semibold text-[color:var(--owc-navy)]">${(c.lifetime_value || 0).toFixed(0)}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-slate-500">No contacts yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {addOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="owc-card-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-[Outfit] font-bold text-lg text-[color:var(--owc-navy)]">Add contact</h3>
              <button onClick={() => setAddOpen(false)}><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <input data-testid={T.contacts.firstName} required autoComplete="given-name" className="owc-input" placeholder="First name *" value={newContact.first_name} onChange={e => setNewContact(c => ({ ...c, first_name: e.target.value }))} />
                <input data-testid={T.contacts.lastName} required autoComplete="family-name" className="owc-input" placeholder="Last name *" value={newContact.last_name} onChange={e => setNewContact(c => ({ ...c, last_name: e.target.value }))} />
              </div>
              <input data-testid={T.contacts.email} type="email" autoComplete="email" className="owc-input" placeholder="Email" value={newContact.email} onChange={e => setNewContact(c => ({ ...c, email: e.target.value }))} />
              <input data-testid={T.contacts.phone} required type="tel" inputMode="tel" autoComplete="tel" className="owc-input" placeholder="Mobile number *" value={newContact.phone} onChange={e => setNewContact(c => ({ ...c, phone: e.target.value }))} />
              <input className="owc-input" placeholder="Address" value={newContact.address} onChange={e => setNewContact(c => ({ ...c, address: e.target.value }))} />
              <input className="owc-input" placeholder="ZIP" value={newContact.zip} onChange={e => setNewContact(c => ({ ...c, zip: e.target.value }))} />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button className="owc-btn-outline text-sm" onClick={() => setAddOpen(false)}>Cancel</button>
              <button data-testid={T.contacts.save} disabled={saving || !newContact.first_name || !newContact.last_name || !newContact.phone} className="owc-btn-accent text-sm inline-flex items-center gap-2" onClick={save}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
